import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { spawnCharacter } from '../entities/npc.js';
import { CHARACTERS } from '../entities/characters.js';
import { HUD } from '../ui/hud.js';
import { getFlag, setFlag, getCrumbs, spendCrumbs, addCrumbs } from '../core/save.js';
import { questsForGiver, acceptQuest, turnIn, isReady, objectiveProgress, QUESTS, flagObjective } from '../core/quests.js';
import { AsteroidRadar } from '../minigames/asteroidRadar.js';
import '../ui/space.css';

// The Spaceport — Earth's launch pad, reached from Uptown. Home of the busted
// Starship Toaster and the "Fix the Spaceship" quest chain: gather a radar
// crystal (Asteroid Radar minigame), hull tape (Scruff the dog pirate), and an
// engine core (cheese stall), install them with Mechanic Rivet, then launch.

const PAD = 0xB6B2A8, PAD_D = 0x8A867C, METAL = 0x9AA4B0, SKY = 0x9FC7E8;
const ENGINE_COST = 20;

export function createSpaceportScene(){ return () => new SpaceportScene(); }

class SpaceportScene {
  constructor(){
    this.panels = [];
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(SKY);
    this.scene.fog = new THREE.Fog(SKY, 30, 70);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 120);
    this.camDist = 16; this.camTarget = new THREE.Vector3(0, 1.6, 2);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildGround();
    this._buildRocket();
    this._buildRadarConsole();
    this._buildCheeseStall();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 9);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.mechanic = this._makeMechanic();  this.scene.add(this.mechanic.root);
    this.scruff = this._makeScruff();       this.scene.add(this.scruff.root);
    this.cheeser = this._makeCheeser();     this.scene.add(this.cheeser.root);

    this.hotspots = [
      { pos: this.mechanic.root.position.clone(), r: 2.4, label: '🔧 talk to Mechanic Rivet', action: () => this._mechanicDialog() },
      { pos: this.scruff.root.position.clone(),   r: 2.4, label: '🏴‍☠️ talk to Scruff Barkbeard', action: () => this._scruffTalk() },
      { pos: this.cheeser.root.position.clone(),  r: 2.4, label: '🧀 engine core stall', action: () => this._buyEngine() },
      { pos: new THREE.Vector3(-6, 0, 2),         r: 2.2, label: '📡 Asteroid Radar', action: () => this._playRadar() },
      { pos: new THREE.Vector3(0, 0, -3),         r: 3.0, label: '🚀 launch pad', action: () => this._tryLaunch() },
      { pos: new THREE.Vector3(0, 0, 12),         r: 2.4, label: '← back to Uptown', action: () => this.manager.goTo('uptown', { from: 'spaceport' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.75));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.9);
    sun.position.set(8, 16, 6); sun.castShadow = true; sun.shadow.mapSize.set(2048, 2048);
    const s = 24; sun.shadow.camera.left = -s; sun.shadow.camera.right = s; sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildGround(){
    const grass = new THREE.Mesh(new THREE.CircleGeometry(30, 48), new THREE.MeshLambertMaterial({ color: C.grass }));
    grass.rotation.x = -Math.PI/2; grass.receiveShadow = true; this.scene.add(grass);
    const pad = new THREE.Mesh(new THREE.CircleGeometry(9, 40), new THREE.MeshLambertMaterial({ color: PAD }));
    pad.rotation.x = -Math.PI/2; pad.position.y = 0.01; pad.receiveShadow = true; this.scene.add(pad);
    // hazard ring around the launch pad
    for (let i = 0; i < 24; i++){
      const a = i / 24 * Math.PI * 2;
      box(0.5, 0.02, 0.5, i % 2 ? 0xF7C948 : PAD_D, Math.cos(a) * 4.2, 0.02, Math.sin(a) * 4.2 - 3, this.scene);
    }
  }

  _buildRocket(){
    const g = new THREE.Group(); g.position.set(0, 0, -3); this.scene.add(g); this.rocket = g;
    cyl(1.2, 1.4, 5.5, 0xE8E4DC, 0, 2.75, 0, g, 24);            // body
    cyl(1.25, 1.25, 0.4, 0xE6537B, 0, 3.6, 0, g, 24);           // red band
    cyl(0, 1.2, 2.0, 0xC94F4F, 0, 6.5, 0, g, 24);               // nose cone
    sphere(0.55, 0x8FD0FF, 0, 4.4, 1.15, g, 16);                // porthole
    for (let i = 0; i < 3; i++){                                 // fins
      const a = i / 3 * Math.PI * 2;
      const fin = box(0.2, 1.6, 1.3, 0xC94F4F, Math.cos(a) * 1.3, 0.9, Math.sin(a) * 1.3, g);
      fin.rotation.y = -a;
    }
    cyl(0.9, 1.3, 0.7, 0x5A5A5A, 0, 0.2, 0, g, 20);             // engine bell
    // gantry tower
    const tower = new THREE.Group(); tower.position.set(2.3, 0, -3); this.scene.add(tower);
    for (const y of [1, 3, 5]) box(2.2, 0.15, 0.15, METAL, 0, y, 0, tower);
    cyl(0.12, 0.12, 6, METAL, 1.1, 3, 0, tower, 8); cyl(0.12, 0.12, 6, METAL, -1.1, 3, 0, tower, 8);
    const sign = textSprite('🚀 Starship Toaster', 26, '#3A2E2A', 4.6, 1.0);
    sign.position.set(0, 8.4, -3); this.scene.add(sign);
  }

  _buildRadarConsole(){
    const g = new THREE.Group(); g.position.set(-6, 0, 2); this.scene.add(g);
    box(1.2, 1.0, 0.8, 0x2E3552, 0, 0.5, 0, g);
    const dish = cyl(0, 0.7, 0.2, 0xB8C4D8, 0, 1.3, 0, g, 16); dish.rotation.x = -0.7;
    cyl(0.05, 0.05, 0.6, METAL, 0, 1.0, 0, g, 6);
    box(0.8, 0.5, 0.06, 0x7CF3B0, 0, 0.6, 0.42, g);            // green screen
  }

  _buildCheeseStall(){
    const g = new THREE.Group(); g.position.set(6, 0, 3); this.scene.add(g);
    box(2.4, 0.2, 1.4, 0x8C6239, 0, 0.9, 0, g);                // counter top
    cyl(0.1, 0.1, 0.9, 0x8C6239, -1.0, 0.45, -0.5, g, 6); cyl(0.1, 0.1, 0.9, 0x8C6239, 1.0, 0.45, -0.5, g, 6);
    box(2.6, 0.12, 1.6, 0xE6537B, 0, 1.8, 0, g);              // striped awning
    box(2.6, 0.12, 1.6, 0xFFF4DF, 0, 1.82, 0, g).scale.set(0.5, 1, 1);
    // wheels of engine-core "cheese"
    sphere(0.35, 0xF4C430, -0.5, 1.1, 0.2, g, 12); sphere(0.3, 0xF4C430, 0.4, 1.05, 0.1, g, 12);
    const sign = textSprite('🧀 Engine Cores', 22, '#6B4A2F', 3.0, 0.8); sign.position.set(6, 2.6, 3); this.scene.add(sign);
  }

  // ---- NPCs ----
  _makeMechanic(){
    const build = (g) => {
      breadSlice(g, C.toast, C.crust, 0.62, 1.1);
      sphere(.07, 0x2E2018, -.17, 1.18, .3, g, 8); sphere(.07, 0x2E2018, .17, 1.18, .3, g, 8);
      cyl(0.5, 0.5, 0.28, 0xF7C948, 0, 1.72, 0, g, 16);        // hard hat
      cyl(0.56, 0.56, 0.05, 0xF7C948, 0, 1.58, 0, g, 16);
      box(0.6, 0.5, 0.08, 0x3D6DA8, 0, 0.5, 0.34, g);          // overalls bib
      stickLegs(g, 0.5);
    };
    return new Townsperson({ name: 'Mechanic Rivet', line: "she'll fly again, just needs a few parts.", pos: new THREE.Vector3(2.4, 0, 4), facing: -Math.PI*0.7, wander: 0, speed: 0, bubbleY: 2.4, build });
  }

  _makeScruff(){
    const build = (g) => {
      box(0.8, 0.7, 1.0, 0x9A6A3C, 0, 0.75, 0, g);             // dog body
      box(0.5, 0.5, 0.5, 0xB07A44, 0, 1.15, 0.5, g);           // head
      box(0.22, 0.18, 0.25, 0x6E4A28, 0, 1.02, 0.82, g);       // snout
      sphere(0.05, 0x2E2018, -0.12, 1.22, 0.72, g, 8);          // eye
      box(0.1, 0.1, 0.02, 0x2E2018, 0.12, 1.22, 0.74, g);      // eyepatch
      box(0.18, 0.28, 0.06, 0x1A1A1A, 0.12, 1.35, 0.55, g);    // patch strap-ish
      const earL = box(0.14, 0.34, 0.06, 0x6E4A28, -0.24, 1.2, 0.4, g); earL.rotation.z = 0.4;
      const earR = box(0.14, 0.34, 0.06, 0x6E4A28, 0.24, 1.2, 0.4, g); earR.rotation.z = -0.4;
      box(0.55, 0.2, 0.55, 0xE6537B, 0, 0.95, 0.4, g);         // red bandana
      for (const x of [-0.28, 0.28]) for (const z of [-0.3, 0.3]) cyl(0.1, 0.1, 0.5, 0x8A5A34, x, 0.25, z, g, 6);
    };
    // Identity from the roster, geometry stays bespoke (see spawnCharacter's build override).
    return spawnCharacter(CHARACTERS.scruff, {
      pos: new THREE.Vector3(-3.5, 0, 6), facing: Math.PI*0.4,
      wander: 1.4, speed: 0.6, bubbleY: 2.0, build
    });
  }

  _makeCheeser(){
    const build = (g) => {
      const wedge = box(0.9, 1.0, 0.7, 0xF4C430, 0, 0.7, 0, g);  // cheese wedge body
      sphere(0.12, 0xE0B020, 0.2, 0.9, 0.36, g, 8); sphere(0.09, 0xE0B020, -0.15, 0.6, 0.36, g, 8); // holes
      sphere(.06, 0x2E2018, -0.16, 1.0, 0.36, g, 8); sphere(.06, 0x2E2018, 0.16, 1.0, 0.36, g, 8);
      box(0.3, 0.05, 0.05, 0x2E2018, 0, 0.82, 0.37, g);
      stickLegs(g, 0.36);
    };
    return new Townsperson({ name: 'Wedge William', line: 'Gouda day! Engine cores, fresh from the crater dairy.', pos: new THREE.Vector3(6, 0, 4.6), facing: -Math.PI*0.8, wander: 0, speed: 0, bubbleY: 1.9, build });
  }

  // ---- interactions ----
  _faceNpc(npc){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(npc.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z); this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
  }

  _playRadar(){
    const g = new AsteroidRadar({ target: 10, duration: 30,
      onWin: () => { setFlag('radarInstalled', true); flagObjective('space-radar', 'radarInstalled'); HUD.refreshQuests(); HUD.say('Radar crystal installed! Tell Mechanic Rivet.'); },
      onClose: () => {} });
    g.mount();
  }

  _scruffTalk(){
    this._faceNpc(this.scruff);
    if (getFlag('hullPatched')){ HUD.say('Scruff: "Hull\'s holdin\' steady, matey. Fair winds!"'); return; }
    setFlag('hullPatched', true); flagObjective('space-hull', 'hullPatched'); HUD.refreshQuests();
    HUD.say('Scruff slaps space-tape on the hull: "Patched \'er up, ye scurvy loaf! Tell yer mechanic."');
  }

  _buyEngine(){
    this._faceNpc(this.cheeser);
    if (getFlag('engineInstalled')){ HUD.say('Wedge William: "Engine core\'s purring. No refunds on cheese!"'); return; }
    if (getCrumbs() < ENGINE_COST){ HUD.say(`Wedge William: "An engine core runs ${ENGINE_COST} crumbs, friend."`); return; }
    spendCrumbs(ENGINE_COST); HUD.refreshCrumbs();
    setFlag('engineInstalled', true); flagObjective('space-engine', 'engineInstalled'); HUD.refreshQuests();
    HUD.say('You install the cheesy engine core. It hums with dairy power! Tell the mechanic.');
  }

  _tryLaunch(){
    if (getFlag('shipLaunched') || getFlag('moonUnlocked')){
      this.manager.goTo('moonTown', { from: 'spaceport' }); return;
    }
    const ready = getFlag('radarInstalled') && getFlag('hullPatched') && getFlag('engineInstalled');
    if (!ready){ HUD.say('The ship still needs work: radar, hull, and engine must all be fixed first.'); this.bread.drawFace('o'); return; }
    setFlag('shipLaunched', true); flagObjective('space-launch', 'shipLaunched'); HUD.refreshQuests();
    HUD.say('🚀 3… 2… 1… liftoff! The Starship Toaster roars up toward the moon!');
    setTimeout(() => this.manager.goTo('moonTown', { from: 'spaceport' }), 1200);
  }

  // ---- Mechanic Rivet quest dialog ----
  _mechanicDialog(){
    this._faceNpc(this.mechanic);
    // first meeting = the inspection itself; acceptQuest is a no-op if already taken
    if (!getFlag('shipInspected')){
      acceptQuest('space-inspect');
      setFlag('shipInspected', true); flagObjective('space-inspect', 'shipInspected'); HUD.refreshQuests();
    }
    this._renderMechanic();
  }

  _renderMechanic(){
    this._clearPanels();
    const { available, active } = questsForGiver('mechanic');
    const rows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isReady(id)){
        rows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>done — claim reward</span></div><button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc} · 🍞 ${q.reward?.crumbs||0}</span></div><button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const body = rows.length ? rows.join('') : `<p class="q-dnone">"She's all fixed up, partner. Safe travels to the moon!"</p>`;
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Mechanic Rivet</h2>
        <div class="cafe-sub">launch pad chief</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); HUD.refreshQuests(); this._renderMechanic(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => { const r = turnIn(b.dataset.id); if (r){ HUD.refreshCrumbs(); HUD.refreshQuests(); this.bread.drawFace('yum'); } this._renderMechanic(); });
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _panel(html){ const el = document.createElement('div'); el.className = 'cafe-panel'; el.innerHTML = html; document.body.appendChild(el); this.panels.push(el); return el; }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager; this.input = manager.input;
    this.input.useCamera(this.camera); this.input.polarRange = [0.3, 0.72];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('the spaceport'); HUD.refreshCrumbs(); HUD.refreshQuests();
    HUD.say('The launch pad! The Starship Toaster needs repairs. Talk to Mechanic Rivet to get started.');
    HUD.setHint('click to walk · talk to Rivet, scan asteroids, gather parts, then launch');
    HUD.hideHintSoon(8000);
  }
  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, [this.mechanic, this.scruff, this.cheeser], this.bread)) return;
    const dir = ray.ray.direction; if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y; if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(Math.max(-13, Math.min(13, p.x)), 0, Math.max(-8, Math.min(13, p.z)));
    HUD.hideHint(); this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    this.mechanic.update(dt, this.bread.root.position);
    this.scruff.update(dt, this.bread.root.position);
    this.cheeser.update(dt, this.bread.root.position);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 2), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){ this.camera.aspect = window.innerWidth / window.innerHeight; this.camera.updateProjectionMatrix(); }
}
